import React from 'react';
import styled from 'styled-components';

import { GNB_HEIGHT, HEADER_HEIGHT, PAGINATION_HEIGHT } from '@/styles';
import CategoryChips from '@/feat-components/ShopSearch/CategoryChips';
import BoardPagination from '@/ui/BoardPagination';
import Card from './kakaoMap/Card';
import KakaoMap from './kakaoMap/KakaoMap';
import ErrorMessage from '@/ui/ErrorMessage';
import { ShippingAddress, SiDo } from '@remodeling/shop';
import { MapSearchFilter } from '@/types';
import { SHOP_COMBINE_FILTER, SHOP_REMODELING_FILTER } from '@/constants';
import { mapType } from '../ShopSearch';
import { CustomScroll } from '@/styles/common';
import { getAsset } from '@/utils';
import TrackingService from '@/services/tracking';
import useMaps from '../hooks/useMaps';
import Image from '@/ui/Image';
import {
  HStack,
  Icongraphy,
  Text,
  VStack,
  Dropdown,
  NewBasicButton,
  Box,
} from '@design-system/pc';

interface MapsProps {
  type?: mapType;
  sidoList?: SiDo[];
  address?: ShippingAddress;
  st: string;
}

const Maps = ({ type = 'entire', sidoList, address, st }: MapsProps) => {
  const {
    center,
    handleChangeDebounceCenter,
    isShowReSearchButton,
    markerListApiData,
    level,
    mapRef,
    isLoading,
    hoverItem,
    shopListNumber,
    sido,
    setSido,
    pages,
    gungu,
    setGungu,
    convertSidoDropdownOptionFormat,
    convertGunGuDropdownOptionFormat,
    clusterListApiData,
    groupCode,
    itemTypes,
    handleClickItemTypes,
    handleClickItemTypesReset,
    handleChangegroupCode,
    setHoverItem,
    setLevel,
    handleClickReSearchButton,
    handleChangeSido,
    handleChangeGunGu,
    handleClickCurrentLoactionButton,
    handleClickCluster,
    handleChangePages,
    openState,
    handleClickMapSearchButton,
    handleToggleMenu,
  } = useMaps({
    sidoList,
    address,
    st,
  });

  return (
    <Container
      height={
        type === 'remodeling'
          ? `calc(100vh - ${GNB_HEIGHT}px - ${HEADER_HEIGHT}px)`
          : `calc(100vh - ${HEADER_HEIGHT}px)`
      }
      {...TrackingService.tagGTM('shop_search')}
    >
      <MenuContainer width={openState ? '454px' : '0px'}>
        <MenuBox
          display={openState ? 'flex' : 'none'}
          backgroundColor="color_chip.white"
        >
          <VStack
            alignment="start"
            space={20}
            width="100%"
            pt="32px"
            pb="20px"
            pr="28px"
            pl="28px"
          >
            <Text variant="subTitle2">매장찾기</Text>
            <HStack space={8}>
              <Dropdown
                variant="list"
                size="md"
                width="100%"
                minWidth="140.55px"
                placeholder="시, 도"
                handleChange={handleChangeSido}
                selectValue={sido?.code}
                option={{
                  data: convertSidoDropdownOptionFormat?.map((sido) => {
                    return {
                      ...sido,
                      ...TrackingService.tagGTM(
                        'shop_search_sido_dropdown_button',
                        {
                          'option-value': sido?.value,
                        },
                      ),
                    };
                  }),
                  styledProps: {
                    width: '195px',
                    height: '168px',
                  },
                }}
              ></Dropdown>
              <Dropdown
                variant="list"
                size="md"
                width="100%"
                minWidth="140.55px"
                placeholder="군,구"
                // sido.code 36인 세종특별자치시만 군,구가 없음
                disabled={!sido || sido?.code === '36'}
                handleChange={handleChangeGunGu}
                selectValue={gungu?.code}
                option={{
                  data: convertGunGuDropdownOptionFormat?.map((gungu) => {
                    return {
                      ...gungu,
                      ...TrackingService.tagGTM(
                        'shop_search_gungu_dropdown_button',
                        {
                          'option-value': gungu?.value,
                        },
                      ),
                    };
                  }),
                  styledProps: {
                    width: '195px',
                    height: '168px',
                  },
                }}
              ></Dropdown>
              <NewBasicButton
                emphasis="low"
                size="md"
                icon={{ variant: 'ic_search', align: 'left' }}
                colorVariant="blue"
                onClick={handleClickMapSearchButton}
                disabled={!sido}
              >
                지도검색
              </NewBasicButton>
            </HStack>
            <FilterContainer>
              {type === 'remodeling' ? (
                <CategoryChips<MapSearchFilter, MapSearchFilter>
                  currentValue={groupCode}
                  type="multi"
                  gap="12px 6px"
                  list={SHOP_REMODELING_FILTER}
                  handleReset={handleClickItemTypesReset}
                  onChange={handleChangegroupCode}
                />
              ) : (
                <CategoryChips<MapSearchFilter, MapSearchFilter>
                  currentValue={itemTypes}
                  type="multi"
                  gap="12px 6px"
                  list={SHOP_COMBINE_FILTER}
                  useCheckIcon
                  hasRefresh={true}
                  handleReset={handleClickItemTypesReset}
                  onChange={handleClickItemTypes}
                />
              )}
            </FilterContainer>
          </VStack>
          <ContentsBox
            height={
              type === 'remodeling'
                ? `calc(100vh - ${GNB_HEIGHT}px - ${HEADER_HEIGHT}px - 240px)`
                : `calc(100vh - ${HEADER_HEIGHT}px - 288px)`
            }
            type={type}
          >
            {!isLoading ? (
              <ResultList
                height={
                  type === 'entire'
                    ? `calc(100% - ${94 + PAGINATION_HEIGHT}px)`
                    : 'calc(100% - 94px)'
                }
              >
                {shopListNumber.totalElements ? (
                  <>
                    <HStack space={2} ml="28px" pt="20px">
                      <Text variant="body1">전체</Text>
                      <Box>
                        <Text variant="headline6">
                          {shopListNumber.totalElements || 0}
                        </Text>
                      </Box>
                      <Text variant="body1">개</Text>
                    </HStack>
                    {markerListApiData.map((item, index) => (
                      <Card
                        key={item.id}
                        type={type}
                        item={item}
                        hoverItem={hoverItem}
                        setHoverItem={setHoverItem}
                        isFirstItem={index === 0}
                        {...TrackingService.tagGTM(
                          `shop_search_shop_detail_card_button`,
                          {
                            'shop-id': item.id,
                          },
                        )}
                      />
                    ))}
                  </>
                ) : (
                  <ErrorMessageBox>
                    <ErrorMessage
                      title="이 지역 내에 검색 결과가 없습니다."
                      subTitle={`지도의 위치를 변경하시거나,\n일부 필터를 변경하여 다시 검색해주세요.`}
                    />
                  </ErrorMessageBox>
                )}
              </ResultList>
            ) : (
              <LoadingBox>
                <Image
                  src={getAsset('/assets/icons/loading-obj-new.gif')}
                  height={'40px'}
                  width="40px"
                  alt="loadingIcon"
                />
              </LoadingBox>
            )}
            {shopListNumber.totalElements ? (
              <PaginationBox
                borderTop="1px solid"
                borderColor="color_chip.gray.50"
              >
                <BoardPagination
                  gtmName="shop_search"
                  page={pages}
                  handlePage={handleChangePages}
                  totalPages={Number(shopListNumber.totalPages) || 1}
                />
              </PaginationBox>
            ) : null}
          </ContentsBox>
        </MenuBox>
        <OnOpenArrow
          backgroundColor="color_chip.white"
          boxShadow="0px 2px 4px rgba(0, 0, 0, 0.25)"
          onClick={handleToggleMenu}
          {...TrackingService.tagGTM(
            `shop_search_${openState ? 'close' : 'open'}_menubox_button`,
          )}
        >
          <IconBox state={openState}>
            <Icongraphy variant="ic_arrow_left_bold" />
          </IconBox>
        </OnOpenArrow>
      </MenuContainer>
      <KakaoMap
        center={center}
        clusterListApiData={clusterListApiData}
        handleChangeCenter={handleChangeDebounceCenter}
        isShowReSearchButton={isShowReSearchButton}
        handleClickReSearchButton={() => {
          handleClickReSearchButton();

          setSido(undefined);
          setGungu(undefined);
        }}
        level={level}
        markerListApiData={markerListApiData}
        setLevel={setLevel}
        hoverItem={hoverItem}
        handleClickCluster={handleClickCluster}
        handleClickCurrentLoactionButton={handleClickCurrentLoactionButton}
        setHoverItem={setHoverItem}
        ref={mapRef}
      />
    </Container>
  );
};

export default Maps;

const Container = styled(Box)`
  width: 100%;
  position: sticky;
  display: flex;
  top: 0px;
  bottom: 0px;
  left: 0px;
`;

const MenuContainer = styled(Box)`
  display: flex;
  align-items: center;
  position: relative;
  /* transition: 0.4s; */
  height: 100%;
  z-index: 9999;
`;

const ErrorMessageBox = styled(Box)`
  width: 100%;
  margin-top: 80px;
  padding: 24px 28px;
  display: flex;
  justify-content: center;
`;

const MenuBox = styled(Box)`
  width: 454px;
  height: 100%;
  animation-fill-mode: forwards;
  flex-direction: column;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.25);
  z-index: 1;
`;

const ContentsBox = styled(Box)`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const OnOpenArrow = styled(Box)`
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 32px;
  height: 80px;
  right: -32px;
`;

const IconBox = styled(Box)<{ state: boolean }>`
  transition-delay: 0.4s;
  transition: 0.4s;
  cursor: pointer;
  transform: ${({ state }) =>
    state ? `rotate3d(0)` : `rotate3d(0, 1, 0, 180deg)`};
`;

const ResultList = styled(Box)`
  width: 100%;
  overflow-y: auto;
  ${CustomScroll}
`;

const LoadingBox = styled(Box)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
`;

const PaginationBox = styled(Box)`
  display: flex;
  width: 100%;
  min-height: 60px;
  box-sizing: border-box;
  justify-content: center;
  align-items: center;
`;

const FilterContainer = styled(Box)`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  width: 100%;
  gap: 12px 6px;
`;
